import React, { useEffect, useState } from "react";

//Material Icons
import { Add, Search } from "@mui/icons-material";

//UI Material
import {
  Button,
  Card,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

//Helpers
import { searchEngine } from "../Helpers/searchEngine";
import { fetchWithTokenJSONPost } from "../Helpers/network";

//Local components
import ErrorBox from "../Components/ErrorBox";
import ClientRow from "../Components/clients-page/ClientRow";
import Selector from "../Components/basics/Selector";
import DataLoader from "../Components/basics/DataLoader";
import SetClientDrawer from "../Components/drawers/SetClientDrawer";
import ElementCard from "../ProComponents/ElementCard";

const idTypes = ["Todos", "Física", "Jurídica", "Otro"];

export default function Clients() {
  // ==========>Component States<==========
  //State for select page
  const [page, setPage] = useState(0);
  //State for manage rows per page
  const [rowsPerPage, setRowsPerPage] = useState(10);
  //State for setting loader for products data
  const [dataLoader, setDataLoader] = useState(false);
  //State for setting error when could not get products data
  const [dataError, setDataError] = useState(false);
  //State for products list
  const [clientsList, setClientsList] = useState([]);
  //State for filter selection
  const [clientTypeSelection, setClientTypeSelection] = useState(0);
  //State for search data by query
  const [searchQuery, setSearchQuery] = useState("");
  //State for open add-client drawer
  const [openClientDrawer, setOpenClientDrawer] = useState(false);

  // ==========>Component UseEffects<==========
  //The useEffect checks for token validation
  useEffect(() => {
    getClientsList();
  }, []);

  // ==========> Component Functions <==========
  //Function to manage page selection
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };
  //Function to control rows per page options
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //Function for data query
  const filterData = (query, data) => {
    if (!query) {
      return data;
    } else {
      return searchEngine(data, query);
    }
  };

  //Function to select filtered data
  const filterClientsByIdType = () => {
    switch (clientTypeSelection) {
      case 0:
        return clientsList;
      case 1:
        return clientsList.filter((p) => p.type === 0);
      case 2:
        return clientsList.filter((p) => p.type === 1);
      case 3:
        return clientsList.filter((p) => p.type === 2);
      default:
        return clientsList;
    }
  };

  //Data in the table
  const dataFiltered = filterData(searchQuery, filterClientsByIdType());

  //Function to get products list
  const getClientsList = async () => {
    setDataLoader(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(`/clients/get`, {
        howMany: 500,
      });
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setClientsList(jsonResponse.data);
      }

      if (requestResponse.status === 401) {
        window.location.href = "/";
      }
    } catch (error) {
      setDataError(true);
    }
    setDataLoader(false);
  };

  if (dataError) {
    return <ErrorBox />;
  }

  // ==========> Component <==========
  return dataLoader ? (
    <DataLoader />
  ) : (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <SetClientDrawer
        open={openClientDrawer}
        setOpen={setOpenClientDrawer}
        getData={getClientsList}
      />
      <Grid item xs={12}>
        <Card raised sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} align="left">
              <ElementCard
                ops={{
                  title: "Registro de Clientes",
                  details: [`${clientsList.length} registrados`],
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Typography fontWeight="bold">
                    Tipo de identificación
                  </Typography>
                  <Selector
                    width="100%"
                    content={idTypes}
                    value={clientTypeSelection}
                    handleChange={(e) => {
                      setClientTypeSelection(e.target.value);
                    }}
                  />
                </Stack>
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Typography fontWeight="bold">Buscar un cliente</Typography>
                  <TextField
                    id="search-bar"
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    variant="outlined"
                    placeholder="Buscar..."
                    fullWidth
                    InputProps={{
                      endAdornment: <Search />,
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Button
                endIcon={<Add />}
                onClick={() => {
                  setOpenClientDrawer(true);
                }}
              >
                Nuevo cliente
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table aria-label="clients-table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <Typography fontWeight="bold">ID</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">Nombre</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">
                          Identificación
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">Teléfono</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">
                          Correo Electrónico
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">Dirección</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">Acciones</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataFiltered.length ? (
                      dataFiltered
                        .sort((a, b) => {
                          if (a.name < b.name) return -1;
                          return 0;
                        })

                        .map((client) => (
                          <ClientRow
                            key={`${client.idDocument}-${client.createdAt}`}
                            client={client}
                            getClients={() => {
                              getClientsList();
                            }}
                          />
                        ))
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={7}>
                          <Typography>No hay clientes registrados</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={dataFiltered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage="Filas"
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
}
